import React, { useState } from 'react';
import { Card, Row, Col, Statistic, Button, Modal, InputNumber, message, Space } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { FinanceSummary } from '../../types/finance';

interface FinanceSummaryCardProps {
  summary: FinanceSummary | null;
  contractAmount: number | null | undefined;
  canEdit: boolean;
  onUpdateContractAmount: (amount: number) => Promise<void>;
}

/**
 * 财务概览卡片组件
 * 显示合同金额、已收款、已退款统计信息
 */
const FinanceSummaryCard: React.FC<FinanceSummaryCardProps> = ({
  summary,
  contractAmount,
  canEdit,
  onUpdateContractAmount,
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [inputAmount, setInputAmount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  /**
   * 格式化金额显示（千分位、两位小数）
   */
  const formatAmount = (amount: number | string | null | undefined): number => {
    if (amount === null || amount === undefined) {
      return 0;
    }
    
    // 如果是字符串，转换为数字
    if (typeof amount === 'string') {
      const parsed = parseFloat(amount);
      return isNaN(parsed) ? 0 : parsed;
    }
    
    return amount;
  };

  /**
   * 打开设置合同金额弹窗
   */
  const handleOpenModal = () => {
    setInputAmount(contractAmount || null);
    setModalVisible(true);
  };

  /**
   * 关闭弹窗
   */
  const handleCloseModal = () => {
    setModalVisible(false);
    setInputAmount(null);
  };

  /**
   * 提交合同金额
   */
  const handleSubmit = async () => {
    if (inputAmount === null || inputAmount === undefined) {
      message.error('请输入合同金额');
      return;
    }

    if (inputAmount <= 0) {
      message.error('合同金额必须大于0');
      return;
    }

    try {
      setLoading(true);
      await onUpdateContractAmount(inputAmount);
      message.success('合同金额设置成功');
      handleCloseModal();
    } catch (error: any) {
      message.error(error.message || '设置失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card 
        className="finance-summary-card" 
        style={{ marginBottom: 16 }}
        title="财务概览"
        extra={
          canEdit && (
            <Button 
              type="link" 
              icon={<EditOutlined />}
              onClick={handleOpenModal}
            >
              {contractAmount ? '修改合同金额' : '设置合同金额'}
            </Button>
          )
        }
      >
        <Row gutter={16}>
          <Col span={8}>
            <Statistic
              title="合同金额"
              value={formatAmount(contractAmount)}
              prefix="¥"
              precision={2}
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="已收款"
              value={formatAmount(summary?.total_received)}
              prefix="¥"
              precision={2}
              valueStyle={{ color: '#3f8600' }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="已退款"
              value={formatAmount(summary?.total_refund)}
              prefix="¥"
              precision={2}
              valueStyle={{ color: '#cf1322' }}
            />
          </Col>
        </Row>
      </Card>

      <Modal
        title={contractAmount ? '修改合同金额' : '设置合同金额'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={handleCloseModal}
        confirmLoading={loading}
        okText="确定"
        cancelText="取消"
        width={400}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <div style={{ marginBottom: 8 }}>
              合同金额（元）<span style={{ color: 'red' }}>*</span>
            </div>
            <InputNumber
              value={inputAmount}
              onChange={(value) => setInputAmount(value)}
              style={{ width: '100%' }}
              min={0.01}
              precision={2}
              step={1000}
              placeholder="请输入合同金额"
              formatter={(value) => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => {
                const parsed = value!.replace(/¥\s?|(,*)/g, '');
                return parsed as any;
              }}
            />
          </div>
          <div style={{ color: '#999', fontSize: 12 }}>
            提示：合同金额为与客户约定的合同总价值
          </div>
        </Space>
      </Modal>
    </>
  );
};

export default FinanceSummaryCard;
